import { useState } from 'react';
import { Button } from 'antd';
import {
    ArrowDownOutlined,
    ArrowRightOutlined,
    CommentOutlined,
    EditOutlined,
    InstagramOutlined,
    MailOutlined,
    MessageOutlined,
    NotificationOutlined,
    PictureOutlined,
    SendOutlined,
    UserAddOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import templatesJson from '../automationList/templates.json';
import { TemplateIcon } from '../flow-v2/list/TemplateGalleryModal';
import AccessOrNot from '../../funcs/accessOrNot';
import showFlowEditorUpgrade from '../../funcs/flowEditorUpgrade';
import QuickAutomationWizard from '../../comps/quickAutomationWizard/main';

const CHANNELS = [
    { key: 'posts', label: 'Posts', icon: <PictureOutlined /> },
    { key: 'stories', label: 'Stories', icon: <VideoCameraOutlined /> },
    { key: 'ads', label: 'Meta Ads', icon: <NotificationOutlined /> },
    { key: 'dms', label: "DM's", icon: <MessageOutlined /> },
];

// Chip colors: comment magenta, follow orange, link indigo, email coral,
// story purple — same palette as the flow node cards.
const COMMENT = { icon: <CommentOutlined />, label: 'Comment on a post', color: '#d62976' };
const AD_COMMENT = { icon: <CommentOutlined />, label: 'Comment on your ad', color: '#d62976' };
const SEND_LINK = { icon: <SendOutlined />, label: 'Send link', color: '#4f5bd5' };
const SEND_LINK_LOC = { icon: <SendOutlined />, label: 'Send link / location', color: '#4f5bd5' };
const FOLLOW = { icon: <UserAddOutlined />, label: 'Follow first', color: '#fa7e1e' };
const CHECK_FOLLOW = { icon: <UserAddOutlined />, label: 'Check follow', color: '#fa7e1e' };
const EMAIL = { icon: <MailOutlined />, label: 'Collect email', color: '#e6683c' };
const STORY = { icon: <InstagramOutlined />, label: 'Reply to story', color: '#962fbf' };
const DM_MESSAGE = { icon: <MessageOutlined />, label: 'Message', color: '#962fbf' };

// Quick recipes per channel, drawn as chip pipelines so anyone gets it at a
// glance. Clicks intentionally do nothing yet — behavior TBD.
const QUICK_BY_CHANNEL = {
    posts: [
        [COMMENT, SEND_LINK],
        [COMMENT, FOLLOW, SEND_LINK],
        [COMMENT, EMAIL],
        [COMMENT, FOLLOW, EMAIL],
    ],
    stories: [
        [STORY, SEND_LINK],
        [STORY, FOLLOW, SEND_LINK],
        [STORY, EMAIL],
        [STORY, FOLLOW, EMAIL],
    ],
    ads: [
        [AD_COMMENT, SEND_LINK],
        [AD_COMMENT, FOLLOW, SEND_LINK],
        [AD_COMMENT, EMAIL],
        [AD_COMMENT, FOLLOW, EMAIL],
    ],
    dms: [
        [DM_MESSAGE, SEND_LINK_LOC],
        [DM_MESSAGE, CHECK_FOLLOW, SEND_LINK_LOC],
    ],
};

// "Start from scratch" per channel → the matching Quick Automation surface.
// The posts grid already reads ?category= from the URL.
const SCRATCH_LINKS = {
    posts: '/ig?tab=1&category=posts',
    stories: '/ig?tab=1&category=stories',
    ads: '/ig?tab=1&category=meta-ads',
    dms: '/quick-dm',
};

// A template's channel comes from its real trigger node — stays honest when
// templates are added or reworked.
const templateChannel = (t) => {
    const ar = t.nodes?.find((n) => n.id === 'node-1')?.data?.auto_reply || {};
    if (ar.story_reply?.enabled) return 'stories';
    if (ar.dm_reply?.enabled) return 'dms';
    return 'posts';
};

const Chip = ({ icon, label, color }) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            borderRadius: 999,
            background: `${color}14`,
            color,
            border: `1px solid ${color}30`,
            fontSize: 11.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }}
    >
        {icon}
        {label}
    </span>
);

const SectionHeader = ({ title, subtitle }) => (
    <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--idm-text)' }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--idm-text-muted)' }}>{subtitle}</div>
    </div>
);

/**
 * Big hover menu under the Instagram Automation card. Left rail picks the
 * channel (Posts / Stories / Meta Ads / DMs); the right side shows Quick
 * Automation recipes and Advanced (flow) templates for that channel.
 */
export default function IgAutomationMenu() {
    const [channel, setChannel] = useState('posts');
    const [wizard, setWizard] = useState(null); // {followFirst} → QuickAutomationWizard open

    const onRecipeClick = (steps) => {
        // Stories/DMs don't have the wizard yet — their scratch surfaces do
        // the job until then.
        if (channel === 'stories') return (window.location.href = SCRATCH_LINKS.stories);
        if (channel === 'dms') return (window.location.href = SCRATCH_LINKS.dms);
        if (steps.includes(EMAIL)) {
            // Quick lead gen = flow machinery (questions + contacts) — the
            // Lead Collection template is that recipe, prefilled.
            if (!AccessOrNot('flow_editor')) return showFlowEditorUpgrade();
            return (window.location.href = `/flow-v2/editor?id=${uuidv4()}&template_id=lead-collection`);
        }
        setWizard({ followFirst: steps.includes(FOLLOW) });
    };
    const templates = templatesJson.templates || [];
    // Comment-trigger templates work on ads too — ads are a comment surface.
    const channelTemplates = templates
        .filter((t) => (channel === 'ads' ? templateChannel(t) === 'posts' : templateChannel(t) === channel))
        .slice(0, 4);

    const openTemplate = (t) => {
        if (!AccessOrNot('flow_editor')) return showFlowEditorUpgrade();
        window.location.href = `/flow-v2/editor?id=${uuidv4()}&template_id=${t.id}`;
    };

    return (
        <div style={{ width: 720, maxWidth: '85vw' }}>
            {/* Header CTA — the card's own click lives in here too, so the
                menu never hides the way into the dashboard. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    paddingBottom: 10,
                    marginBottom: 12,
                    borderBottom: '1px solid var(--idm-border)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <InstagramOutlined style={{ color: '#d62976', fontSize: 16, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--idm-text)' }}>Instagram Automation</div>
                        <div style={{ fontSize: 11, color: 'var(--idm-text-muted)' }}>
                            Pick a template below &amp; automate <ArrowDownOutlined style={{ fontSize: 10 }} /> — or
                        </div>
                    </div>
                </div>
                <Button type="primary" size="small" onClick={() => (window.location.href = '/ig')}>
                    Go to IG Automation →
                </Button>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
            {/* Channel rail */}
            <div
                style={{
                    width: 132,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRight: '1px solid var(--idm-border)',
                    paddingRight: 12,
                }}
            >
                {CHANNELS.map((ch) => {
                    const active = ch.key === channel;
                    return (
                        <div
                            key={ch.key}
                            onMouseEnter={() => setChannel(ch.key)}
                            onClick={() => setChannel(ch.key)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '8px 10px',
                                borderRadius: 10,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: active ? 700 : 500,
                                color: active ? 'var(--idm-brand-600, #4f46e5)' : 'var(--idm-text-muted)',
                                background: active ? 'var(--idm-brand-50, #eef2ff)' : 'transparent',
                            }}
                        >
                            {ch.icon}
                            {ch.label}
                        </div>
                    );
                })}
            </div>

            {/* Quick + Advanced for the hovered channel */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <SectionHeader title="Quick Automation" subtitle="Best for sending a link or collecting an email" />
                {/* Meta Ads: setup lives on the posts grid — scratch only. */}
                {channel !== 'ads' && (QUICK_BY_CHANNEL[channel] || []).map((steps, idx) => (
                    <div
                        key={idx}
                        onClick={() => onRecipeClick(steps)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 6,
                            padding: '7px 10px',
                            marginBottom: 6,
                            borderRadius: 10,
                            border: '1px solid var(--idm-border)',
                            background: 'var(--idm-surface)',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--idm-brand-400, #818cf8)')}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--idm-border)')}
                    >
                        {steps.map((step, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {i > 0 && <ArrowRightOutlined style={{ fontSize: 10, color: 'var(--idm-text-faint)' }} />}
                                <Chip {...step} />
                            </span>
                        ))}
                    </div>
                ))}

                {/* Start from scratch — always available, the only quick
                    option on Meta Ads. */}
                <div
                    onClick={() => (window.location.href = SCRATCH_LINKS[channel] || '/ig?tab=1')}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '7px 10px',
                        marginBottom: 6,
                        borderRadius: 10,
                        border: '1px dashed var(--idm-border-strong)',
                        background: 'var(--idm-surface)',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--idm-text-muted)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--idm-brand-400, #818cf8)';
                        e.currentTarget.style.color = 'var(--idm-brand-600, #4f46e5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--idm-border-strong)';
                        e.currentTarget.style.color = 'var(--idm-text-muted)';
                    }}
                >
                    <EditOutlined />
                    Start from scratch
                    <ArrowRightOutlined style={{ fontSize: 10, marginLeft: 'auto' }} />
                </div>

                <div style={{ height: 1, background: 'var(--idm-border)', margin: '12px 0' }} />

                <SectionHeader
                    title="Advanced Automation (flows)"
                    subtitle="Best for complex funnels with follow-ups, conditions & AI"
                />
                {channelTemplates.map((t) => (
                    <div
                        key={t.id}
                        onClick={() => openTemplate(t)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '6px 8px',
                            marginBottom: 2,
                            borderRadius: 10,
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--idm-brand-50, #eef2ff)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                        <TemplateIcon template={t} size={28} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--idm-text)' }}>{t.name}</div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: 'var(--idm-text-muted)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {t.description}
                            </div>
                        </div>
                    </div>
                ))}
                <Button
                    type="link"
                    size="small"
                    style={{ padding: '0 8px', fontSize: 12 }}
                    onClick={() => (window.location.href = '/ig?tab=2')}
                >
                    View all templates →
                </Button>
            </div>
            </div>

            <QuickAutomationWizard open={!!wizard} config={wizard || {}} onClose={() => setWizard(null)} />
        </div>
    );
}
